/**
 * ResendReportEmailButton — one-click resend of the client-notification
 * email for a single Incident / DAR / Maintenance record.
 *
 * Placed next to the Edit button on DAR.jsx, Incidents.jsx, Maintenance.jsx.
 * POSTs `/api/reports/<kind>/<id>/resend-email` and surfaces the outcome as
 * a toast. Skips fanning-out to no-one gracefully (backend returns
 * `skipped: true` when there are no recipients).
 */
import { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { GhostButton } from "./Primitives";

export default function ResendReportEmailButton({ kind, itemId, testId }) {
  const [sending, setSending] = useState(false);

  const onClick = async () => {
    if (sending) return;
    setSending(true);
    try {
      const r = await api.post(`/reports/${kind}/${itemId}/resend-email`);
      const d = r.data?.detail || {};
      if (d.skipped) {
        toast.info(`No email sent — ${d.reason || "no recipients"}`);
      } else if (d.emailed) {
        toast.success(`Email sent to ${d.emailed} recipient${d.emailed === 1 ? "" : "s"}`);
      } else if (d.sent) {
        toast.success("Notification email sent");
      } else {
        toast.info("Email request queued");
      }
    } catch (e) {
      toast.error(errorMessage(e, "Resend failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <GhostButton
      onClick={onClick}
      disabled={sending}
      data-testid={testId || `resend-email-${kind}-${itemId}`}
      title="Resend the client notification email"
    >
      <Mail className="size-3.5" />
    </GhostButton>
  );
}
